import React, { useState, useEffect } from 'react';

interface PVQSample {
  filename: string;
  displayName: string;
  description: string;
}

interface Props {
  onSampleSelected: (pvqData: string) => void;
}

const PVQSampleLoader: React.FC<Props> = ({ onSampleSelected }) => {
  const [samples, setSamples] = useState<PVQSample[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSample, setSelectedSample] = useState<string>('');

  // Sample files from the pvq directory - real PVQ submissions
  const availableSamples: PVQSample[] = [
    { filename: 'subject1.json', displayName: 'Subject 01', description: 'Basic PVQ submission (2.2KB)' },
    { filename: 'subject02.json', displayName: 'Subject 02', description: 'Moderate Risk Confidential - Jennifer Martinez (9KB)' },
    { filename: 'subject3.json', displayName: 'Subject 03', description: 'Standard submission with employment history (2.5KB)' },
    { filename: 'subject04.json', displayName: 'Subject 04', description: 'Complex case with multiple validation points (9.2KB)' },
    { filename: 'subject5.json', displayName: 'Subject 05', description: 'Comprehensive submission with verifiers (10.6KB)' },
    { filename: 'subject6.json', displayName: 'Subject 06', description: 'Cross-section validation example (2.7KB)' },
    { filename: 'subject7.json', displayName: 'Subject 07', description: 'Date consistency test case (2.3KB)' },
    { filename: 'subject8.json', displayName: 'Subject 08', description: 'Selection logic verification (11.1KB)' },
    { filename: 'subject9.json', displayName: 'Subject 09', description: 'Full comprehensive case (11.3KB)' },
    { filename: 'subject10.json', displayName: 'Subject 10', description: 'Complex multi-section validation (11.7KB)' },
    { filename: 'subject11.json', displayName: 'Subject 11', description: 'Employment history deep dive (11.8KB)' },
    { filename: 'subject12.json', displayName: 'Subject 12', description: 'Education and residence alignment (12KB)' },
    { filename: 'subject13.json', displayName: 'Subject 13', description: 'Military service correlation (12.7KB)' },
    { filename: 'subject17.json', displayName: 'Subject 17', description: 'Foreign influence considerations (10KB)' },
    { filename: 'subject20.json', displayName: 'Subject 20', description: 'Financial record validation (9.8KB)' }
  ];

  useEffect(() => {
    setSamples(availableSamples);
  }, []);

  const loadSampleFile = async (filename: string) => {
    setLoading(true);
    try {
      // Try to load the actual file from the public directory
      const response = await fetch(`/pvq_samples/${filename}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load ${filename}: ${response.status}`);
      }
      
      const fileContent = await response.text();
      
      // Try to parse as JSON to validate
      const jsonData = JSON.parse(fileContent);
      
      // Load the actual file content
      onSampleSelected(JSON.stringify(jsonData, null, 2));
      setSelectedSample(filename);
    } catch (error) {
      console.error('Error loading sample file:', error);
      
      // Fallback to simulated data if file loading fails
      console.log('Falling back to simulated data for:', filename);
      try {
        const mockData = await simulateFileLoad(filename);
        onSampleSelected(JSON.stringify(mockData, null, 2));
        setSelectedSample(filename);
      } catch (mockError) {
        console.error('Mock data generation failed:', mockError);
        alert(`Error loading sample file ${filename}. Please try again or paste data manually.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Simulate loading different sample files with realistic data
  const simulateFileLoad = async (filename: string): Promise<any> => {
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay

    const baseData = {
      formMetadata: {
        formId: `PVQ-2024-${filename.replace('.json', '').toUpperCase()}`,
        formVersion: "1.2",
        dateSubmitted: "2023-05-20T13:45:22Z",
        formType: "Initial",
        investigationType: "Tier 2",
        positionRisk: "Moderate Risk",
        clearanceLevel: "Confidential"
      }
    };

    // Customize data based on filename to create different validation scenarios
    switch (filename) {
      case 'subject02.json':
        return {
          ...baseData,
          section_9_citizenship: {
            birth_location: "Germany",
            citizenship_selection: "US citizen by birth in US territory"
          },
          section_13a_employment: [
            {
              employer: "Ford Motor Company",
              start_date: "2024-05-01",
              end_date: "2024-09-01",
              position: "Engineer"
            },
            {
              employer: "Unemployment",
              start_date: "2023-08-01",
              end_date: "2024-09-01",
              position: "Unemployed"
            }
          ],
          section_15_military: [
            {
              service_start: "2014-08-01",
              service_end: "2018-08-01",
              discharge_date: "2018-10-01"
            }
          ]
        };

      case 'subject04.json':
        return {
          ...baseData,
          section_12_education: [
            {
              school: "University of Maryland",
              start_date: "2021-08-01",
              end_date: "2025-05-01",
              verifier: {
                name: "John Smith Sr",
                relationship: "Father",
                phone: "I don't know",
                address: "Unknown"
              }
            }
          ],
          section_18_relatives: [
            {
              name: "John Smith Sr",
              relationship: "Father"
            }
          ]
        };

      case 'subject1.json':
        return {
          ...baseData,
          section_11_residence: [
            {
              address: "123 Main St, College Park, MD",
              start_date: "2023-07-01",
              end_date: "2025-07-01"
            }
          ],
          section_12_education: [
            {
              school: "University of Maryland",
              start_date: "2019-08-01",
              end_date: "2023-05-01"
            }
          ]
        };

      default:
        return {
          ...baseData,
          section_9_citizenship: {
            birth_location: "United States",
            citizenship_selection: "US citizen by birth in US territory"
          },
          section_13a_employment: [
            {
              employer: "Generic Corporation",
              start_date: "2020-01-01",
              end_date: "Present",
              position: "Software Engineer"
            }
          ]
        };
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
        <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        Load Sample PVQ File
      </h3>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="sample-select" className="block text-sm font-medium text-blue-800 mb-2">
            Choose from available PVQ samples:
          </label>
          <select
            id="sample-select"
            value={selectedSample}
            onChange={(e) => setSelectedSample(e.target.value)}
            className="w-full px-3 py-2 border border-blue-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">Select a sample file...</option>
            {samples.map((sample) => (
              <option key={sample.filename} value={sample.filename}>
                {sample.displayName} - {sample.description}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => selectedSample && loadSampleFile(selectedSample)}
          disabled={!selectedSample || loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </span>
          ) : (
            'Load Selected Sample'
          )}
        </button>

        <div className="text-xs text-blue-700">
          <p>
            <strong>Note:</strong> Sample files demonstrate various validation scenarios including 
            date conflicts, verifier issues, and cross-section inconsistencies commonly flagged by FSOs.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PVQSampleLoader;